import { createFileRoute, Link } from '@tanstack/react-router';
import { requireAuth } from '../lib/requireAuth';
import { useWebsiteData } from '../hooks/useWebsiteData';
import { usePageMeta } from '../hooks/usePageMeta';
import { AdminNav } from '../components/AdminNav';
import { useState, useEffect } from 'react';

const LIVE_ENDPOINT = '/.netlify/functions/get-portfolio';

function cloudinaryThumb(url: string, w: number, h: number): string {
  return url.replace('/upload/', `/upload/w_${w},h_${h},c_fill,q_auto/`);
}

export const Route = createFileRoute('/projects/')({
  beforeLoad: () => requireAuth('/projects'),
  component: ProjectsPage,
});

function ProjectsPage() {
  usePageMeta('Projects | Boyden Renovations', undefined, true);
  const { data: sections, isLoading } = useWebsiteData(LIVE_ENDPOINT);
  const [deletedMessage, setDeletedMessage] = useState<string | null>(() => {
    const name = sessionStorage.getItem('deletedProject');
    if (name) sessionStorage.removeItem('deletedProject');
    return name;
  });

  useEffect(() => {
    if (!deletedMessage) return;
    const t = setTimeout(() => setDeletedMessage(null), 4000);
    return () => clearTimeout(t);
  }, [deletedMessage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xs tracking-[3px] uppercase text-neutral-dark/40">
          Loading...
        </p>
      </div>
    );
  }

  const folders = Object.entries(sections || {}).filter(([, section]) =>
    Array.isArray(section._images)
  );

  return (
    <div className="min-h-screen font-sans text-neutral-dark">
      <AdminNav />
      <div className="py-10 px-8 mx-auto w-full max-w-316">
        <h1 className="text-3xl font-serif tracking-widest uppercase mb-8">
          Projects
        </h1>

        {deletedMessage && (
          <div className="mb-6 px-4 py-3 border border-neutral-dark/20 bg-neutral-dark/5">
            <p className="text-xs tracking-[2px] uppercase text-neutral-dark/60">
              <span className="text-neutral-dark">{deletedMessage}</span> was deleted
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map(([key, section]) => {
            const heroImage =
              section._images.find((img) => img.context?.Hero === 'true') ??
              section._images[0];
            const projectName =
              section._images[0]?.metadata?.website ??
              section._metadata.name.replace(/_/g, ' ');
            const count = section._images.length;

            return (
              <Link
                key={key}
                to="/projects/$folderId"
                params={{ folderId: key }}
                className="group relative overflow-hidden border border-neutral-dark/20 hover:border-neutral-dark/50 transition-all duration-300"
                style={{ height: 150 }}
              >
                {/* Background thumbnail */}
                {heroImage && (
                  <img
                    src={cloudinaryThumb(heroImage.secure_url, 800, 300)}
                    alt={projectName}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-neutral-dark/55 group-hover:bg-neutral-dark/45 transition-colors duration-300" />
                {/* Content */}
                <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                  <div className="flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-serif tracking-widest leading-tight truncate">
                        {section._metadata.name.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs tracking-[2px] uppercase text-white/55 truncate mt-1">
                        {projectName}
                      </p>
                    </div>
                    <p className="text-xs tracking-[3px] uppercase text-white/55 shrink-0">
                      {count} image{count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
